import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import React, { useEffect, useMemo, useState } from 'react'
import { findClosestSize } from 'src/components/Modals/StorageSizeModal/constants'
import { SearchHood, SearchPageCity } from 'src/constants/searchPages'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import useCommon from 'src/hooks/useCommon'
import useLazyNamespace from 'src/hooks/useLazyNamespace'
import { useGetPrice } from 'src/hooks/usePrice'
import {
  ListingVariantProvider,
  useListingVariant
} from 'src/lib/listingVariants'
import { getVertical } from 'src/utils/pixels/enrichment'
import { viewItem } from 'src/utils/pixels/spotmeEvents'
import { isTraditionalVariant } from 'src/utils/pricing'
import {
  IConfiguration,
  ILocation,
  IUser,
  Maybe,
  StorageTime
} from '../../types/graphql'
import Reviews from '../Review/Reviews'
import Breadcrumb from './Breadcrumb'
import ContactUs from './ContactUs'
import Header from './Header'
// ImagesV2 holds the LCP cover photo and passes `priority={index === 0}` to
// next/image. Wrapping it in dynamic() suppresses Next's SSR
// `<link rel="preload" as="image">` hint, forcing Chrome to discover the
// image at Low priority (Lighthouse "fetchpriority=high" check FAILS).
// Keep this as a direct import so the preload hint is emitted.
import { getLocationProfile } from 'src/utils/profile'
import HostLogisticsServices from './HostLogisticsServices'
import ImagesV2 from './ImagesWrapper'
import LeadFormInlineCta from './LeadFormInlineCta'
import PriceCard from './PriceCard'
import Reports from './Reports'
import SecurityAndSupport from './SecurityAndSupport'
import SimilarLocations from './SimilarLocations'
import SizeSection from './SizeSection'
import UnavailableBanner, { type UnavailableReason } from './UnavailableBanner'
import User from './User'
import WrapperWA from './WrapperWA'

/**
 * DOM id on the SimilarLocations section so <UnavailableBanner>'s primary
 * CTA can smooth-scroll into it. Exported as a constant so both ends stay
 * in lockstep (renaming one without the other silently breaks the scroll).
 */
const SIMILAR_SECTION_ID = 'similar-listings'

// StorageSizeModal is interaction-gated (only mounts when `sizeGuideOpen`
// flips true on click) so the SSR/CSR initial trees match — keep dynamic.
const StorageSizeModal = dynamic(
  () => import('../Modals/StorageSizeModal/StorageSizeModal')
)

const GET_LOCATION = gql`
  query GetLocation($locationId: String!, $configurationId: String) {
    location(locationId: $locationId, configurationId: $configurationId) {
      id
    }
  }
`

type IProps = {
  location: ILocation
  configurationId?: string
  adminPageDisplay?: boolean
  breadcrumbs: [SearchPageCity | null, SearchHood | null]
  /**
   * When set, renders the listing in an "unavailable" state:
   * <UnavailableBanner> at the top, PriceCard CTAs replaced with "Ver
   * espacios similares", URL stays stable. See root CLAUDE.md →
   * "Unavailable listings". Null/undefined renders the standard listing.
   */
  unavailableReason?: UnavailableReason | null
}

/** Editorial hairline divider — replaces the previous heavy blue rule. */
const SectionDivider = ({ className = '' }: { className?: string }) => (
  <hr
    className={`my-8 h-px w-full border-0 bg-ink-100/15 md:my-12 ${className}`}
    aria-hidden="true"
  />
)

/**
 * Reviews, with the host reply anonymized on every variant that withholds the
 * landlord. Lives inside `<ListingVariantProvider>` so it can read the same
 * variant table the rest of the page renders from — a named, linked
 * "Respuesta de <operador>" would hand back exactly the identity the variant
 * removed.
 */
function ListingReviews({ user }: { user: Maybe<IUser> | undefined }) {
  const variant = useListingVariant()
  return (
    <Reviews
      user={user}
      anonymizeOwner={variant.sections.user.variant !== 'host'}
    />
  )
}

function Details({
  location,
  configurationId,
  adminPageDisplay,
  breadcrumbs,
  unavailableReason
}: IProps) {
  const reportsRef = React.useRef<HTMLDivElement>(null)
  const { id } = location

  // Only manage selected configuration state if there are multiple configurations
  const hasMultipleConfigs = (location.configurations?.length ?? 0) > 1

  // Use selected config only when we have multiple configurations
  const activeConfig = (
    hasMultipleConfigs
      ? (location.configurations?.find(
          (config) => config.id === configurationId
        ) ??
        location.defaultConfiguration ??
        location.configurations?.[0])
      : (location.defaultConfiguration ?? location.configurations?.[0])
  ) as IConfiguration

  const logger = useLogger()
  const { loading, currentUser } = useAuthProvider()
  const [getLocation, { error: locationError }] = useLazyQuery(GET_LOCATION, {
    fetchPolicy: 'cache-and-network'
  })

  useEffect(() => {
    if (locationError) logger.error('Error fetching location', locationError)
  }, [locationError, logger])
  const { configLocMap } = useCommon()
  const { getIsBrokerMode } = useGetPrice()

  // Compute display mode flags
  const displayMode = useMemo(
    () => ({
      isBrokerMode: getIsBrokerMode(activeConfig),
      isTraditionalMode: isTraditionalVariant(activeConfig),
      hasMultipleConfigs,
      showAdmin: adminPageDisplay || currentUser?.isAdmin
    }),
    [
      activeConfig,
      hasMultipleConfigs,
      adminPageDisplay,
      currentUser,
      getIsBrokerMode
    ]
  )

  const { isBrokerMode, isTraditionalMode, showAdmin } = displayMode
  const isRestrictedMode = (isBrokerMode || isTraditionalMode) && !showAdmin

  // Owner / admin bypass for the unavailable state. When the viewer is the
  // listing's host or a SpotMe admin, render the listing as normal — they
  // need the standard PriceCard (with edit/admin controls + the full data
  // tree) to manage the listing, not the renter-facing "Ya no disponible"
  // banner. Matches the existing PriceCard `userIsOwner` pattern. Team
  // members are not bypassed client-side (no IUser.teamIds field on
  // currentUser); they can manage from the dashboard.
  const viewerIsOwnerOrAdmin =
    !!currentUser &&
    (currentUser.id === location.submittedBy || !!currentUser.isAdmin)
  const effectiveUnavailableReason = viewerIsOwnerOrAdmin
    ? null
    : (unavailableReason ?? null)
  const isUnavailable = !!effectiveUnavailableReason

  // Broker / Traditional listings — when unavailable, prefer the WhatsApp
  // escape hatch (Fernando via corporate WA → LangGraph) over the
  // alternatives carousel. Those funnels are specialist-mediated, so the
  // next-best action is the asesor, not browsing similar listings. Monthly
  // listings keep similars-first. See spotme-client/CLAUDE.md →
  // "Unavailable listings".
  const preferWhatsappOnUnavailable = isBrokerMode || isTraditionalMode
  // Size guide CTA is hidden on parking listings — it's a volume guide
  // ("how much fits in a small/medium/large bedroom") that's irrelevant
  // for parking, where the user already knows they need 1–N spots. The
  // SizeSection page block stays visible (it correctly shows vehicle/fleet
  // pictograms for parking).
  const hideSizeGuideCta = getVertical(activeConfig?.locationType) === 'parking'

  // The size-guide CTA prompt (CTA_QUESTION) is first-paint copy, so it reads
  // from `details` (SSR'd on this page). The heavy `size_guide` namespace
  // (~12 kB of size categories) is consumed only by the interaction-gated
  // StorageSizeModal — dropped from this page's serverSideTranslations and
  // lazy-loaded below the moment the modal opens (enabled=sizeGuideOpen), so
  // visitors who never open the size guide pay nothing. See
  // docs/performance/large-page-data.md.
  const { t } = useTranslation('details')
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false)
  const sizeGuideReady = useLazyNamespace('size_guide', sizeGuideOpen)
  const sizeGuideDefaults = useMemo(() => {
    const w = activeConfig?.size?.width || 1
    const l = activeConfig?.size?.length || 1
    return findClosestSize(w * l, !!activeConfig?.canStoreVehicle)
  }, [activeConfig])

  // Lifted from PriceCard so WrapperWA can access for enriched WA messages
  const [quantity, setQuantity] = useState(1)
  const [storageTime, setStorageTime] = useState<StorageTime | ''>('')

  useEffect(() => {
    if (activeConfig) {
      viewItem({
        location,
        config: activeConfig,
        spaceType: configLocMap(activeConfig)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (loading) return
    getLocation({ variables: { locationId: id, configurationId } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  return (
    <ListingVariantProvider location={location} configuration={activeConfig}>
      {/* Editorial frame — content rail uses generous gutters and a 12-col
          rhythm so the gallery + sticky rail read as a magazine spread, not a
          dashboard panel. `md:pt-5` adds a small breath between the fixed nav
          and the breadcrumb without floating the breadcrumb. */}
      <div className="mx-auto max-w-7xl px-5 md:mt-[90px] md:px-10 md:pt-5">
        {/* Breadcrumb — desktop only, eyebrow rhythm */}
        <Breadcrumb
          location={location}
          activeConfig={activeConfig}
          className="mb-4 hidden md:flex"
          breadcrumbs={breadcrumbs}
        />

        {/* Unavailable state — renders above the gallery so the renter sees
            it before reading anything else. Photos / metadata stay visible
            below so they understand which listing this was, and the
            SimilarLocations rail at the bottom carries the alternatives.
            Hidden for the listing's owner and SpotMe admins — they get the
            standard listing surface so they can manage / edit. */}
        {isUnavailable && effectiveUnavailableReason && (
          <UnavailableBanner
            reason={effectiveUnavailableReason}
            city={location.addressDetails?.city}
            similarSectionId={SIMILAR_SECTION_ID}
            preferWhatsapp={preferWhatsappOnUnavailable}
            notify={
              activeConfig
                ? {
                    locationId: location.id,
                    configId: activeConfig.id,
                    locationType: activeConfig.locationType,
                    coordinates: location.coordinates
                  }
                : undefined
            }
          />
        )}

        {/* Image Gallery — Images.tsx owns its own mobile break-out
            (`-mx-5`). Don't wrap in another negative-margin div or you stack
            two breakouts and overflow the viewport. Greyscale + opacity
            tint for unavailable listings so the photos read as "previous"
            without redrawing the whole layout. */}
        <div className={isUnavailable ? 'opacity-80 grayscale' : undefined}>
          <ImagesV2 location={location} configuration={activeConfig} />
        </div>

        {/* Two-column rail. Mobile sits tight under the gallery (`mt-4`)
            so the eyebrow + H1 read as a caption to the photos rather
            than a separate block. Desktop keeps the wider editorial gap. */}
        <div className="mt-4 flex flex-col md:mt-12 md:flex-row md:gap-12">
          {/* Left — main editorial content */}
          <div className="w-full md:w-[60%]">
            <Header
              location={location}
              activeConfig={activeConfig}
              reportsRef={reportsRef}
            />

            {/* Mobile size guide CTA — kept editorial: hairline + small caps */}
            {!isBrokerMode && !adminPageDisplay && !hideSizeGuideCta && (
              <button
                type="button"
                onClick={() => setSizeGuideOpen(true)}
                className="mt-6 flex w-full items-center justify-center gap-2 border border-ink-100/40 py-3 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-500 md:hidden"
              >
                <i className="si si-ruler-combined text-base" />
                {t('CTA_QUESTION')}
              </button>
            )}

            {/* Section divider above the User block. Renders for Monthly
                (host masthead) and Broker / Fleet (specialist card) so
                they share the same single-hairline rhythm. Hidden for
                Traditional non-admin views — the User section is set to
                `variant: 'hidden'` there (no host, no specialist), so an
                always-on divider would leave an orphaned hairline above
                the Reports block. */}
            {!isTraditionalMode && <SectionDivider />}

            <User
              location={location}
              isBrokerMode={isRestrictedMode}
              configuration={activeConfig}
              quantity={quantity}
              storageTime={storageTime || undefined}
            />

            {/* Mobile-only secondary email CTA — sits in the listing body
                between User (specialist on broker, hidden on traditional)
                and Reports (description / amenities), so it's visible
                near the top of the page on mobile without having to
                scroll past the whole listing body. Self-renders only on
                broker / traditional variants (the only listings whose
                PriceCard CTA is the WA pill). WhatsApp stays the primary
                path in the sticky bar; this block is the structured
                email alternative for users who don't want to WhatsApp.
                Desktop equivalent lives directly inside PriceCard
                (outlined secondary button below the WA pill). */}
            {!adminPageDisplay && (
              <LeadFormInlineCta
                location={location}
                configuration={activeConfig}
              />
            )}

            <Reports
              location={location}
              configurationId={configurationId}
              isBrokerMode={isBrokerMode}
              reportsRef={reportsRef}
            />
          </div>

          {/* Right — sticky price rail (desktop only).
              Offset is nav-relative (--nav-h + 1.5rem of breathing room), not
              the hardcoded 110px it used to be. Two reasons: the hardcoded
              value silently drifts if the navbar height ever changes (see
              spotme-client/CLAUDE.md → "Nav Height"), and every px here is a
              px of rail height the viewport no longer has — the tallest
              variant (Monthly + multi-unit + fleet pill) is right at the
              limit on a 13" laptop.
              max-h + overflow-y is the backstop, not the primary fix: the
              rail is trimmed to fit unaided on every common laptop viewport,
              but a 1366x768 screen (~660px of viewport after browser chrome)
              is still ~20px short, and the rail grows well past any viewport
              when the fleet LeadForm expands inline inside the card. Without
              this cap that overflow is simply unreachable — a sticky element
              never scrolls into view. Above ~680px of viewport the cap never
              engages, so this is invisible on normal screens. */}
          <div className="hidden pt-2 md:block md:w-[40%]">
            <div className="sticky top-[calc(var(--nav-h)+1.5rem)] max-h-[calc(100vh-var(--nav-h)-3rem)] overflow-y-auto overscroll-contain">
              <PriceCard
                location={location}
                adminPageDisplay={adminPageDisplay}
                activeConfig={activeConfig}
                hideMobilePriceCard
                quantity={quantity}
                setQuantity={setQuantity}
                storageTime={storageTime}
                setStorageTime={setStorageTime}
                unavailableReason={effectiveUnavailableReason}
                similarSectionId={SIMILAR_SECTION_ID}
              />

              {/* Size guide CTA — editorial outline, small-caps label */}
              {!isBrokerMode && !adminPageDisplay && !hideSizeGuideCta && (
                <button
                  type="button"
                  onClick={() => setSizeGuideOpen(true)}
                  className="mt-4 flex w-full items-center justify-center gap-2 border border-ink-100/40 bg-white-500 py-3 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-500"
                >
                  <i className="si si-ruler-combined text-base" />
                  {t('CTA_QUESTION')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Section rail — generous editorial breathing room */}
        <div className="mt-12 md:mt-24">
          <SizeSection
            location={location}
            activeConfig={activeConfig}
            isBrokerMode={isBrokerMode}
          />

          <HostLogisticsServices location={location} />

          {/* Team-first: `getLocationProfile` returns the host profile with
              the team's name + icon when the listing belongs to one (and the
              real user id, so review pagination still resolves). */}
          <ListingReviews
            user={getLocationProfile(location, configurationId).user}
          />

          {!adminPageDisplay && <SecurityAndSupport locationId={id} />}
        </div>
      </div>

      {/* Full-bleed sections — cream/grey backdrops replace the old
          blue→green gradient (off-brand) with a calmer editorial palette. */}
      {!adminPageDisplay && (
        <>
          <div
            id={SIMILAR_SECTION_ID}
            className="mt-16 scroll-mt-[calc(var(--nav-h)+24px)] bg-cream-300 py-14 md:mt-28 md:py-28"
          >
            <div className="mx-auto max-w-7xl px-5 md:px-10">
              <SimilarLocations
                location={location}
                activeConfig={activeConfig}
              />
            </div>
          </div>

          <div className="bg-grey-250 pt-14 pb-40 md:py-28">
            <div className="mx-auto max-w-7xl px-5 md:px-10">
              <ContactUs location={location} />
            </div>
          </div>
        </>
      )}

      {/* Mobile Price Card - Only render mobile bottom bar */}
      <PriceCard
        location={location}
        adminPageDisplay={adminPageDisplay}
        activeConfig={activeConfig}
        hideDesktopPriceCard
        quantity={quantity}
        setQuantity={setQuantity}
        storageTime={storageTime}
        setStorageTime={setStorageTime}
        unavailableReason={effectiveUnavailableReason}
        similarSectionId={SIMILAR_SECTION_ID}
      />

      <WrapperWA
        location={location}
        activeConfig={activeConfig}
        storageTime={storageTime}
        quantity={quantity}
      />

      {/* Size Guide Modal — gated on `sizeGuideReady` so the lazily-fetched
          `size_guide` namespace is on the i18n instance before the modal's
          t() calls run (no raw-key flash). */}
      {sizeGuideOpen && sizeGuideReady && (
        <StorageSizeModal
          open={sizeGuideOpen}
          handleClose={() => setSizeGuideOpen(false)}
          defaultCategory={sizeGuideDefaults.categoryId}
          defaultSizeId={sizeGuideDefaults.sizeId}
          surface="listing_detail"
        />
      )}
    </ListingVariantProvider>
  )
}

export default React.memo(Details)
